import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Modal from '../elements/Modal';
import Button from '../elements/Button';
import Icon from './Icon';
import { apiRequest } from '../../utils';
import notificationConfig from '../../config/notificationConfig.json';

// Pops modal-flagged notifications (is_modal) on the home screen. The backend
// only returns ones the user hasn't dismissed yet, so they keep showing on
// every home visit until the user closes each one once. Multiple pending
// announcements are shown one at a time, newest first. Content reuses the same
// notificationConfig + i18n mapping as the /notifications feed page.
//
// ★ Also re-checks when the app comes back to the foreground, and whenever `refreshKey` changes:
// a raffle win ("come to the stand NOW") must not wait for the next visit to Home. Home bumps the
// key while a draw is under way (see /api/my-prizes raffle_live).
const AnnouncementModal = ({ refreshKey }) => {
  const { t } = useTranslation(['common', 'notifications']);
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const again = () => { if (document.visibilityState === 'visible') setTick((n) => n + 1); };
    window.addEventListener('focus', again);
    document.addEventListener('visibilitychange', again);
    return () => {
      window.removeEventListener('focus', again);
      document.removeEventListener('visibilitychange', again);
    };
  }, []);

  useEffect(() => {
    let active = true;
    apiRequest('GET', '/notifications/modal')
      .then((data) => { if (active) setQueue(data?.notifications || []); })
      .catch(() => { /* non-blocking: home still renders without the modal */ });
    return () => { active = false; };
  }, [refreshKey, tick]);

  const current = queue[0] || null;

  const details = useMemo(() => {
    if (!current) return null;
    const meta = notificationConfig[current.message_name] || {};
    const icon = meta.icon || 'megaphone';

    const dynamicData = {};
    if (meta.dynamicKey1 && current.dynamic_key_1) dynamicData[meta.dynamicKey1] = current.dynamic_key_1;
    if (meta.dynamicKey2 && current.dynamic_key_2) dynamicData[meta.dynamicKey2] = current.dynamic_key_2;
    if (current.redirect_key_1) dynamicData['redirect_key_1'] = current.redirect_key_1;

    const message = t(`notifications:${current.message_name}`, dynamicData);

    let redirectUrl = meta.redirectUrl || null;
    if (redirectUrl) {
      redirectUrl = redirectUrl.replace(/{{(\w+)}}/g, (_, key) => dynamicData[key] || key);
    }
    if (redirectUrl === '#') redirectUrl = null;

    return { message, icon, redirectUrl, body: current.body };
  }, [current, t]);

  if (!current || !details) return null;

  // Drop the current item locally and tell the backend to stop resurfacing it.
  const dismiss = async () => {
    const id = current.id;
    setQueue((q) => q.slice(1));
    try {
      await apiRequest('POST', `/notifications/${id}/dismiss`);
    } catch { /* already advanced locally; a stale row just re-pops next load */ }
  };

  const view = async () => {
    const url = details.redirectUrl;
    await dismiss();
    if (url) navigate(url);
  };

  return (
    <Modal show title={t(current.message_name === 'raffle_won' ? 'raffleWonTitle' : 'announcementTitle')}
           onClose={dismiss} hideFooter>
      <div className="text-center">
        <Icon name={details.icon} theme="primary" style={{ fontSize: '2rem' }} className="mb-3" />
        <p className={details.body ? 'mb-2' : 'mb-4'}>{details.message}</p>
        {/* Free text of a back-office announcement (bu_announcement), same as the feed. */}
        {details.body && (
          <p className="mb-4" style={{ whiteSpace: 'pre-wrap' }}>{details.body}</p>
        )}
        <div className="d-flex justify-content-center gap-2">
          {details.redirectUrl ? (
            <>
              <Button theme="secondary" onClick={dismiss} mb="0" styles={{}}>{t('dismiss')}</Button>
              <Button onClick={view} mb="0" styles={{}}>{t('view')}</Button>
            </>
          ) : (
            <Button onClick={dismiss} mb="0" styles={{}}>{t('gotIt')}</Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AnnouncementModal;
